import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Search, Eye, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/site/Brand";
import DetailDialog from "@/components/admin/DetailDialog";
import { api } from "@/lib/api";

const TYPES = ["all", "patient", "franchise", "partner", "vendor"];
const STATUS_CLS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" };

const toCsv = (rows) => {
  const head = ["type", "name", "organization", "email", "phone", "city", "details", "message", "status", "created_at"];
  const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((r) => head.map((h) => esc(h === "details" ? Object.entries(r.details ?? {}).map(([k, v]) => `${k}: ${v}`).join("; ") : r[h])).join(","));
  return [head.join(","), ...lines].join("\n");
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-white border border-[#14083E]/10 p-5" data-testid={testid}>
    <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-slate-600">{label}</p>
    <p className="mt-2 font-display text-4xl font-extrabold text-[#14083E]">{value ?? 0}</p>
  </div>
);

export default function Admin() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/admin/enrolments", { params: type === "all" ? {} : { type } }), api.get("/admin/stats")]);
      setItems(Array.isArray(l.data) ? l.data : []);
      setStats(s.data ?? {});
    } catch (e) {
      if (e.response?.status === 401) nav("/admin/login");
    }
  }, [type, nav]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return items.filter((i) => !s || [i.name, i.email, i.city, i.organization, i.phone].join(" ").toLowerCase().includes(s));
  }, [items, q]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enrolments/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/admin/enrolments/${id}`);
    toast.success("Deleted");
    load();
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    localStorage.removeItem("img_admin_token");
    nav("/admin/login");
  };
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(filtered)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `img-health-enrolments-${type}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="min-h-screen bg-[#F4F3F8]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#14083E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <Logo className="h-8 sm:h-9" />
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-[#14083E]/15 px-4 py-2 text-sm font-semibold text-[#14083E] hover:bg-[#14083E] hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-[#14083E]">Enquiries &amp; Enrolments</h1>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3">
          <Stat label="Total" value={stats.total} testid="stat-total" />
          <Stat label="Patient" value={stats.patient} testid="stat-patient" />
          <Stat label="Franchise" value={stats.franchise} testid="stat-franchise" />
          <Stat label="Partner" value={stats.partner} testid="stat-partner" />
          <Stat label="Vendor" value={stats.vendor} testid="stat-vendor" />
          <Stat label="Pending" value={stats.pending} testid="stat-pending" />
        </div>
        <div className="mt-8 flex flex-col md:flex-row gap-3 md:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`admin-filter-${t}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${type === t ? "bg-[#14083E] text-white" : "bg-white text-[#14083E] hover:bg-[#14083E]/5"}`}>{t}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1 md:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, city" className="pl-9 h-10 rounded-full bg-white" data-testid="admin-search-input" />
            </div>
            <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-gradient text-white px-4 h-10 text-sm font-semibold">
              <Download className="w-4 h-4" /> CSV
            </button>
          </div>
        </div>
        <div className="mt-6 rounded-2xl bg-white border border-[#14083E]/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="admin-submissions-table">
            <thead className="text-left text-slate-600 font-mono-x text-[11px] uppercase tracking-[0.15em] border-b border-[#14083E]/10">
              <tr><th className="p-4">Type</th><th className="p-4">Applicant</th><th className="p-4">Contact</th><th className="p-4">City</th><th className="p-4">Date</th><th className="p-4">Status</th><th className="p-4" /></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-slate-600" data-testid="admin-empty-state">No applications yet.</td></tr>
              )}
              {filtered.map((i) => (
                <tr key={i.id} className="border-b border-[#14083E]/5 hover:bg-[#FAF0F6]/60" data-testid={`admin-row-${i.id}`}>
                  <td className="p-4 capitalize font-semibold text-[#c8106a]">{i.type}</td>
                  <td className="p-4"><p className="font-semibold text-[#14083E]">{i.name}</p><p className="text-slate-600 text-xs">{i.type === "patient" ? i.details?.service : i.organization}</p></td>
                  <td className="p-4"><p>{i.email}</p><p className="text-slate-600 text-xs">{i.phone}</p></td>
                  <td className="p-4">{i.city}</td>
                  <td className="p-4 text-slate-600">{new Date(i.created_at).toLocaleDateString()}</td>
                  <td className="p-4">
                    <Select value={i.status} onValueChange={(s) => setStatus(i.id, s)}>
                      <SelectTrigger className={`h-8 w-32 rounded-full border-0 capitalize text-xs font-semibold ${STATUS_CLS[i.status]}`} data-testid={`admin-status-${i.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{["pending", "approved", "rejected"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setView(i)} data-testid={`admin-view-${i.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-[#14083E]/5" aria-label="View"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => remove(i.id)} data-testid={`admin-delete-${i.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-red-50 text-red-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DetailDialog item={view} onClose={() => setView(null)} />
    </main>
  );
}
